import React, { useState, useEffect, useCallback } from 'react';
import { 
  Search, 
  Play, 
  X, 
  Youtube, 
  ChevronRight,
  ChevronLeft,
  ArrowLeft,
  Loader2,
  ListMusic,
  Users,
  Video,
  Bookmark,
  BookmarkCheck,
  Plus,
  Trash2,
  Bell,
  BellOff,
  RefreshCw
} from 'lucide-react';
import { useMiniPlayer } from '../../context/MiniPlayerContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const YouTubeSection = () => {
  // Mini player context for persistent video playback
  const { playVideo: playInMiniPlayer } = useMiniPlayer();
  
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState(null);
  const [activeView, setActiveView] = useState('channels'); // 'channels', 'search', 'playlist', 'saved', 'channel-detail'
  const [playlistId, setPlaylistId] = useState('');
  const [playlistData, setPlaylistData] = useState(null);
  const [savedVideos, setSavedVideos] = useState([]);
  const [artistFilter, setArtistFilter] = useState(null);
  
  // Track current video list for queue
  const [currentVideoList, setCurrentVideoList] = useState([]);
  
  // Channels state
  const [subscribedChannels, setSubscribedChannels] = useState([]);
  const [channelSearchQuery, setChannelSearchQuery] = useState('');
  const [channelSearchResults, setChannelSearchResults] = useState([]);
  const [subscriptionFeed, setSubscriptionFeed] = useState([]);
  const [showChannelSearch, setShowChannelSearch] = useState(false);
  
  // Channel detail view state
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [channelVideos, setChannelVideos] = useState([]);

  // Auth token helper — reads from localStorage, same key used by UserAuthContext
  const getAuthToken = () => localStorage.getItem('auth_token') || '';
  const tokenParam = () => `token=${getAuthToken()}`;

  // Check API status and load data on mount
  useEffect(() => {
    checkApiStatus();
    loadSavedVideos();
    loadSubscribedChannels();
  }, []);

  // Load subscription feed when channels view is active
  useEffect(() => {
    if (activeView === 'channels' && subscribedChannels.length > 0) {
      loadSubscriptionFeed();
    }
  }, [activeView, subscribedChannels.length]);

  const checkApiStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/youtube/status`);
      const data = await response.json();
      setApiStatus(data);
    } catch (error) {
      console.error('Failed to check API status:', error);
      setApiStatus({ connected: false, api_configured: false });
    }
  };

  const loadSavedVideos = async () => {
    try {
      const res = await fetch(`${API_URL}/api/youtube/saved-videos?${tokenParam()}`);
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'success') setSavedVideos(data.videos || []);
      }
    } catch (error) {
      console.error('Failed to load saved videos:', error);
    }
  };

  // Channel subscription functions
  const loadSubscribedChannels = async () => {
    try {
      const response = await fetch(`${API_URL}/api/youtube/subscriptions?${tokenParam()}`);
      const data = await response.json();
      if (data.status === 'success') {
        setSubscribedChannels(data.channels || []);
      }
    } catch (error) {
      console.error('Failed to load subscriptions:', error);
    }
  };

  const loadSubscriptionFeed = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/youtube/subscriptions/feed?max_per_channel=5&${tokenParam()}`);
      const data = await response.json();
      if (data.status === 'success') {
        setSubscriptionFeed(data.videos || []);
      }
    } catch (error) {
      console.error('Failed to load feed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load videos from a specific channel (100 videos)
  const loadChannelVideos = async (channel) => {
    setLoading(true);
    setSelectedChannel(channel);
    setActiveView('channel-detail');
    
    try {
      const response = await fetch(
        `${API_URL}/api/youtube/channel-videos?channel_id=${encodeURIComponent(channel.channel_id)}&max_results=100`
      );
      const data = await response.json();
      if (data.status === 'success') {
        setChannelVideos(data.videos || []);
        // Update video list for queue
        setCurrentVideoList(data.videos.map(v => ({
          ...v,
          songTitle: v.title,
          artist: v.channel
        })));
      }
    } catch (error) {
      console.error('Failed to load channel videos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Go back from channel detail view
  const goBackToChannels = () => {
    setSelectedChannel(null);
    setChannelVideos([]);
    setActiveView('channels');
  };

  const searchChannels = async (e) => {
    e?.preventDefault();
    if (!channelSearchQuery.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/youtube/search-channels?query=${encodeURIComponent(channelSearchQuery)}&max_results=10&${tokenParam()}`
      );
      const data = await response.json();
      if (data.status === 'success') {
        setChannelSearchResults(data.channels || []);
      }
    } catch (error) {
      console.error('Channel search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToChannel = async (channel) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/youtube/subscriptions?${tokenParam()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          channel_id: channel.channel_id,
          channel_title: channel.channel_title,
          thumbnail: channel.thumbnail
        })
      });
      const data = await response.json();
      if (data.status === 'success' || data.status === 'already_subscribed') {
        await loadSubscribedChannels();
        setChannelSearchResults(prev => prev.map(c => 
          c.channel_id === channel.channel_id ? { ...c, is_subscribed: true } : c
        ));
      }
    } catch (error) {
      console.error('Subscribe failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const unsubscribeFromChannel = async (channelId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/youtube/subscriptions/${channelId}?${tokenParam()}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.status === 'success') {
        setSubscribedChannels(prev => prev.filter(c => c.channel_id !== channelId));
        setChannelSearchResults(prev => prev.map(c => 
          c.channel_id === channelId ? { ...c, is_subscribed: false } : c
        ));
      }
    } catch (error) {
      console.error('Unsubscribe failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveVideo = async (video) => {
    try {
      const res = await fetch(`${API_URL}/api/youtube/saved-videos?${tokenParam()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          video_id: video.id,
          title: video.title,
          thumbnail: video.thumbnail,
          channel: video.channel,
          channel_id: video.channel_id,
          duration: video.duration,
          view_count: video.view_count,
          published_at: video.published_at
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.status === 'success' || data.status === 'already_saved') {
          setSavedVideos(prev => prev.some(v => v.video_id === video.id) ? prev : [...prev, { ...video, video_id: video.id }]);
        }
      }
    } catch (error) {
      console.error('Save video failed:', error);
    }
  };

  const removeVideo = async (videoId) => {
    try {
      const res = await fetch(`${API_URL}/api/youtube/saved-videos/${videoId}?${tokenParam()}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setSavedVideos(prev => prev.filter(v => (v.video_id || v.id) !== videoId));
      }
    } catch (error) {
      console.error('Remove video failed:', error);
    }
  };

  const isVideoSaved = (videoId) => {
    return savedVideos.some(v => (v.video_id || v.id) === videoId);
  };

  // Search videos
  const searchVideos = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/youtube/search?query=${encodeURIComponent(searchQuery)}&max_results=20`
      );
      const data = await response.json();
      if (data.status === 'success') {
        setSearchResults(data.videos);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get filtered videos from playlist (moved before playVideo since it uses this)
  const getFilteredVideos = useCallback(() => {
    if (!playlistData?.videos) return [];
    if (!artistFilter) return playlistData.videos;
    return playlistData.videos.filter(v => v.artist === artistFilter);
  }, [playlistData, artistFilter]);

  // Load playlist
  const loadPlaylist = async (e) => {
    e?.preventDefault();
    if (!playlistId.trim()) return;

    setLoading(true);
    setArtistFilter(null);
    try {
      const response = await fetch(
        `${API_URL}/api/youtube/playlist/${encodeURIComponent(playlistId)}?max_results=500`
      );
      const data = await response.json();
      if (data.status === 'success') {
        setPlaylistData(data);
        if (data.videos) {
          setCurrentVideoList(data.videos.map(v => ({
            ...v,
            songTitle: v.title,
            artist: v.channel
          })));
        }
      }
    } catch (error) {
      console.error('Failed to load playlist:', error);
    } finally {
      setLoading(false);
    }
  };

  // Play video in mini player with queue support
  const playVideo = useCallback((video, videoList = null) => {
    let queue = videoList || currentVideoList;
    
    if (!queue || queue.length === 0) {
      if (activeView === 'channels' && subscriptionFeed.length > 0) {
        queue = subscriptionFeed;
      } else if (activeView === 'search' && searchResults.length > 0) {
        queue = searchResults;
      } else if (activeView === 'playlist' && playlistData?.videos) {
        queue = artistFilter ? getFilteredVideos() : playlistData.videos;
      } else if (activeView === 'saved' && savedVideos.length > 0) {
        queue = savedVideos;
      } else {
        queue = [video];
      }
    }
    
    const formattedQueue = queue.map(v => ({
      ...v,
      songTitle: v.title,
      artist: v.channel,
      embed_url: v.embed_url || `https://www.youtube.com/embed/${v.id}`
    }));
    
    const index = formattedQueue.findIndex(v => v.id === video.id);
    
    const formattedVideo = {
      ...video,
      songTitle: video.title,
      artist: video.channel,
      embed_url: video.embed_url || `https://www.youtube.com/embed/${video.id}`
    };
    
    playInMiniPlayer(formattedVideo, formattedQueue, index >= 0 ? index : 0);
  }, [playInMiniPlayer, currentVideoList, activeView, subscriptionFeed, searchResults, playlistData, savedVideos, artistFilter, getFilteredVideos]);

  // Format number
  const formatNumber = (num) => {
    if (!num) return '0';
    const n = parseInt(num);
    if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1) + 'K';
    return n.toString();
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Video Card Component
  const VideoCard = ({ video, onPlay }) => (
    <div
      data-testid={`video-card-${video.id}`}
      className="group bg-gray-900/50 rounded-xl overflow-hidden hover:bg-gray-800/70 transition-all duration-200 cursor-pointer border border-gray-800 hover:border-gray-600"
      onClick={() => onPlay(video)}
    >
      <div className="relative aspect-video">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center">
            <Play className="w-7 h-7 text-white ml-1" fill="white" />
          </div>
        </div>
        <button
          data-testid={`save-video-${video.id}`}
          onClick={(e) => {
            e.stopPropagation();
            isVideoSaved(video.id) ? removeVideo(video.id) : saveVideo(video);
          }}
          className="absolute top-2 right-2 p-2 rounded-lg bg-black/60 hover:bg-black/80 transition-colors"
        >
          {isVideoSaved(video.id) ? (
            <BookmarkCheck className="w-4 h-4 text-red-500" />
          ) : (
            <Bookmark className="w-4 h-4 text-white" />
          )}
        </button>
      </div>
      <div className="p-3">
        <h3 className="text-white font-medium line-clamp-2 text-sm mb-1">
          {video.title}
        </h3>
        <p className="text-gray-400 text-xs mb-1">{video.channel}</p>
        <p className="text-gray-500 text-xs">{formatDate(video.published_at)}</p>
      </div>
    </div>
  );

  // Channel Card Component
  const ChannelCard = ({ channel, onSubscribe, onUnsubscribe, isSubscribed }) => (
    <div
      data-testid={`channel-card-${channel.channel_id}`}
      className="bg-gray-900/50 rounded-xl p-4 border border-gray-800 hover:border-gray-600 transition-all"
    >
      <div className="flex items-center gap-4">
        <img
          src={channel.thumbnail}
          alt={channel.channel_title}
          className="w-14 h-14 rounded-full object-cover"
        />
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-medium truncate">{channel.channel_title}</h3>
          <p className="text-gray-400 text-sm">{formatNumber(channel.subscriber_count)} subscribers</p>
        </div>
        {isSubscribed || channel.is_subscribed ? (
          <button
            onClick={() => onUnsubscribe(channel.channel_id)}
            className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center gap-2 transition-colors text-sm"
          >
            <BellOff className="w-4 h-4" />
            Subscribed
          </button>
        ) : (
          <button
            onClick={() => onSubscribe(channel)}
            className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-2 transition-colors text-sm"
          >
            <Bell className="w-4 h-4" />
            Subscribe
          </button>
        )}
      </div>
    </div>
  );

  // Subscribed Channel Mini Card
  const SubscribedChannelCard = ({ channel, onUnsubscribe, onClick }) => (
    <div
      data-testid={`subscribed-channel-${channel.channel_id}`}
      className="group bg-gray-900/50 rounded-xl p-3 border border-gray-800 hover:border-red-500/50 hover:bg-gray-800/70 transition-all flex items-center gap-3 cursor-pointer"
      onClick={() => onClick(channel)}
    >
      <img
        src={channel.thumbnail}
        alt={channel.channel_title}
        className="w-12 h-12 rounded-full object-cover ring-2 ring-transparent group-hover:ring-red-500/50 transition-all"
      />
      <div className="flex-1 min-w-0">
        <h4 className="text-white font-medium truncate text-sm group-hover:text-red-400 transition-colors">{channel.channel_title}</h4>
        <p className="text-gray-500 text-xs">{formatNumber(channel.subscriber_count)} subs</p>
      </div>
      <div className="flex items-center gap-1">
        <ChevronRight className="w-4 h-4 text-gray-600 group-hover:text-red-400 transition-colors" />
        <button
          onClick={(e) => {
            e.stopPropagation();
            onUnsubscribe(channel.channel_id);
          }}
          className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-red-600/20 transition-all"
          title="Unsubscribe"
        >
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>
    </div>
  );

  // Artist Card for Playlist
  const ArtistCard = ({ artist, onClick }) => (
    <div
      onClick={onClick}
      className="group bg-gray-900/50 rounded-xl p-4 hover:bg-gray-800/70 transition-all duration-200 cursor-pointer border border-gray-800 hover:border-gray-600 flex items-center gap-4"
    >
      <img
        src={artist.thumbnail}
        alt={artist.name}
        className="w-14 h-14 rounded-lg object-cover"
      />
      <div className="flex-1 min-w-0">
        <h3 className="text-white font-medium truncate">{artist.name}</h3>
        <p className="text-gray-400 text-sm">{artist.videos.length} videos</p>
      </div>
      <ChevronRight className="w-5 h-5 text-gray-500 group-hover:text-white transition-colors" />
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-red-600 flex items-center justify-center">
            <Youtube className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">YouTube</h2>
            <p className="text-gray-400 text-xs">Search and watch videos</p>
          </div>
        </div>
        
        {/* API Status */}
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${
            apiStatus?.connected ? 'bg-green-500' : 
            apiStatus?.quota_exceeded ? 'bg-yellow-500' : 'bg-red-500'
          }`} />
          <span className={`text-xs ${
            apiStatus?.quota_exceeded ? 'text-yellow-400' : 'text-gray-400'
          }`}>
            {apiStatus?.connected ? 'Connected' : 
             apiStatus?.quota_exceeded ? 'Quota Exceeded (resets midnight PT)' : 
             'Disconnected'}
          </span>
        </div>
      </div>

      {/* Quota Warning Banner */}
      {apiStatus?.quota_exceeded && (
        <div className="bg-yellow-600/20 border border-yellow-500/30 rounded-lg p-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
            <Youtube className="w-4 h-4 text-yellow-400" />
          </div>
          <div>
            <p className="text-yellow-400 text-sm font-medium">YouTube API Daily Quota Exceeded</p>
            <p className="text-yellow-400/70 text-xs">Your subscribed channels and saved videos are still available. Search and new channel features will resume at midnight Pacific Time.</p>
          </div>
        </div>
      )}

      {/* View Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        {[
          { id: 'channels', label: `Channels (${subscribedChannels.length})`, icon: Bell },
          { id: 'search', label: 'Search', icon: Search },
          { id: 'playlist', label: 'Playlist', icon: ListMusic },
          { id: 'saved', label: `Saved (${savedVideos.length})`, icon: Bookmark }
        ].map((tab) => (
          <button
            key={tab.id}
            data-testid={`youtube-tab-${tab.id}`}
            onClick={() => setActiveView(tab.id)}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors whitespace-nowrap text-sm ${
              activeView === tab.id 
                ? 'bg-red-600 text-white' 
                : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Channels View */}
      {activeView === 'channels' && (
        <div data-testid="channels-view">
          {/* Header with Add Channel button */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-red-500" />
              Subscribed Channels
            </h3>
            <div className="flex gap-2">
              <button
                onClick={loadSubscriptionFeed}
                disabled={loading || subscribedChannels.length === 0}
                className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 rounded-lg flex items-center gap-2 transition-colors text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <button
                data-testid="add-channel-button"
                onClick={() => setShowChannelSearch(!showChannelSearch)}
                className={`px-3 py-1.5 rounded-lg flex items-center gap-2 transition-colors text-sm ${
                  showChannelSearch 
                    ? 'bg-gray-700 text-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                {showChannelSearch ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                {showChannelSearch ? 'Close' : 'Add Channel'}
              </button>
            </div>
          </div>

          {/* Channel Search Panel */}
          {showChannelSearch && (
            <div className="mb-6 p-4 bg-gray-900/50 rounded-xl border border-gray-800">
              <form onSubmit={searchChannels} className="mb-4">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    <input
                      data-testid="channel-search-input"
                      type="text"
                      value={channelSearchQuery}
                      onChange={(e) => setChannelSearchQuery(e.target.value)}
                      placeholder="Search for channels..."
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading || !channelSearchQuery.trim()}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    Search
                  </button>
                </div>
              </form>

              {channelSearchResults.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-medium text-gray-400">Search Results</h4>
                  {channelSearchResults.map((channel) => (
                    <ChannelCard
                      key={channel.channel_id}
                      channel={channel}
                      onSubscribe={subscribeToChannel}
                      onUnsubscribe={unsubscribeFromChannel}
                      isSubscribed={channel.is_subscribed}
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Subscribed Channels Grid */}
          {subscribedChannels.length > 0 && (
            <div className="mb-6">
              <p className="text-xs text-gray-500 mb-3">Click a channel to view their videos</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {subscribedChannels.map((channel) => (
                  <SubscribedChannelCard
                    key={channel.channel_id}
                    channel={channel}
                    onUnsubscribe={unsubscribeFromChannel}
                    onClick={loadChannelVideos}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Subscription Feed */}
          {subscriptionFeed.length > 0 ? (
            <div data-testid="subscription-feed">
              <h3 className="text-base font-semibold mb-4 flex items-center gap-2 text-white">
                <Video className="w-5 h-5 text-red-500" />
                Latest Videos ({subscriptionFeed.length})
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {subscriptionFeed.map((video) => (
                  <VideoCard key={video.id} video={video} onPlay={playVideo} />
                ))}
              </div>
            </div>
          ) : subscribedChannels.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-14 h-14 text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-400 mb-2">No subscriptions yet</h3>
              <p className="text-gray-500 text-sm mb-4">Subscribe to channels to see their latest videos</p>
              <button
                onClick={() => setShowChannelSearch(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg font-medium transition-colors inline-flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Your First Channel
              </button>
            </div>
          ) : !loading && (
            <div className="text-center py-6">
              <p className="text-gray-500 text-sm">Click "Refresh" to load latest videos</p>
            </div>
          )}
        </div>
      )}

      {/* Search View */}
      {activeView === 'search' && (
        <>
          <form onSubmit={searchVideos} className="mb-6">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  data-testid="search-input"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for videos..."
                  className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                />
              </div>
              <button
                data-testid="search-button"
                type="submit"
                disabled={loading || !searchQuery.trim()}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Search
              </button>
            </div>
          </form>

          {searchResults.length > 0 && (
            <div data-testid="search-results">
              <h3 className="text-base font-semibold mb-4 flex items-center gap-2 text-white">
                <Video className="w-5 h-5 text-red-500" />
                Search Results ({searchResults.length})
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {searchResults.map((video) => (
                  <VideoCard key={video.id} video={video} onPlay={playVideo} />
                ))}
              </div>
            </div>
          )}

          {searchResults.length === 0 && !loading && (
            <div className="text-center py-12">
              <Youtube className="w-14 h-14 text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-400 mb-2">Search for videos</h3>
              <p className="text-gray-500 text-sm">Enter a search term to find YouTube videos</p>
            </div>
          )}
        </>
      )}

      {/* Playlist View */}
      {activeView === 'playlist' && (
        <>
          <form onSubmit={loadPlaylist} className="mb-6">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <ListMusic className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <input
                  data-testid="playlist-input"
                  type="text"
                  value={playlistId}
                  onChange={(e) => setPlaylistId(e.target.value)}
                  placeholder="Enter YouTube playlist ID..."
                  className="w-full bg-gray-900 border border-gray-800 rounded-lg pl-10 pr-4 py-2.5 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-red-600 transition-colors"
                />
              </div>
              <button
                data-testid="load-playlist-button"
                type="submit"
                disabled={loading || !playlistId.trim()}
                className="px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg font-medium transition-colors flex items-center gap-2 text-sm"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ListMusic className="w-4 h-4" />}
                Load
              </button>
            </div>
            <p className="text-gray-500 text-xs mt-2">
              Tip: The playlist ID is the part after "list=" in a YouTube playlist URL
            </p>
          </form>

          {playlistData && (
            <div data-testid="playlist-content">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-white">{playlistData.playlist_title || 'Playlist'}</h3>
                  <p className="text-gray-400 text-sm">{playlistData.total} videos</p>
                </div>
                {artistFilter && (
                  <button
                    onClick={() => setArtistFilter(null)}
                    className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center gap-2 transition-colors text-sm"
                  >
                    <X className="w-4 h-4" />
                    Clear: {artistFilter}
                  </button>
                )}
              </div>

              {!artistFilter && playlistData.artists && (
                <div className="mb-6">
                  <h4 className="text-base font-semibold mb-3 flex items-center gap-2 text-white">
                    <Users className="w-5 h-5 text-red-500" />
                    Artists ({playlistData.artists.length})
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {playlistData.artists.slice(0, 12).map((artist) => (
                      <ArtistCard 
                        key={artist.name} 
                        artist={artist} 
                        onClick={() => setArtistFilter(artist.name)}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h4 className="text-base font-semibold mb-4 flex items-center gap-2 text-white">
                  <Video className="w-5 h-5 text-red-500" />
                  Videos ({getFilteredVideos().length})
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {getFilteredVideos().map((video) => (
                    <VideoCard key={video.id} video={video} onPlay={playVideo} />
                  ))}
                </div>
              </div>
            </div>
          )}

          {!playlistData && !loading && (
            <div className="text-center py-12">
              <ListMusic className="w-14 h-14 text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-400 mb-2">Load a playlist</h3>
              <p className="text-gray-500 text-sm">Enter a YouTube playlist ID to view its contents</p>
            </div>
          )}
        </>
      )}

      {/* Saved View */}
      {activeView === 'saved' && (
        <>
          {savedVideos.length > 0 ? (
            <div data-testid="saved-videos">
              <h3 className="text-base font-semibold mb-4 flex items-center gap-2 text-white">
                <Bookmark className="w-5 h-5 text-red-500" />
                Saved Videos ({savedVideos.length})
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {savedVideos.map((video) => (
                  <VideoCard key={video.id} video={video} onPlay={playVideo} />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Bookmark className="w-14 h-14 text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-400 mb-2">No saved videos</h3>
              <p className="text-gray-500 text-sm">Click the bookmark icon on any video to save it</p>
            </div>
          )}
        </>
      )}

      {/* Channel Detail View */}
      {activeView === 'channel-detail' && selectedChannel && (
        <div data-testid="channel-detail-view">
          <div className="flex items-center gap-4 mb-6">
            <button
              data-testid="back-to-channels"
              onClick={goBackToChannels}
              className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <img
              src={selectedChannel.thumbnail}
              alt={selectedChannel.channel_title}
              className="w-14 h-14 rounded-full object-cover ring-2 ring-red-500/50"
            />
            <div className="flex-1">
              <h3 className="text-xl font-bold text-white">{selectedChannel.channel_title}</h3>
              <p className="text-gray-400 text-sm">
                {formatNumber(selectedChannel.subscriber_count)} subscribers • {selectedChannel.video_count || channelVideos.length} videos
              </p>
            </div>
            <button
              onClick={() => unsubscribeFromChannel(selectedChannel.channel_id)}
              className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg flex items-center gap-2 transition-colors text-sm"
            >
              <BellOff className="w-4 h-4" />
              Subscribed
            </button>
          </div>

          {channelVideos.length > 0 ? (
            <div>
              <h4 className="text-base font-semibold mb-4 flex items-center gap-2 text-white">
                <Video className="w-5 h-5 text-red-500" />
                Latest Videos ({channelVideos.length})
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {channelVideos.map((video) => (
                  <VideoCard key={video.id} video={video} onPlay={playVideo} />
                ))}
              </div>
            </div>
          ) : !loading && (
            <div className="text-center py-12">
              <Video className="w-14 h-14 text-gray-700 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-400 mb-2">No videos found</h3>
              <p className="text-gray-500 text-sm">This channel hasn't uploaded any videos yet</p>
            </div>
          )}
        </div>
      )}

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center pointer-events-none">
          <div className="bg-gray-900 rounded-xl p-4 flex items-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-red-500" />
            <span>Loading...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default YouTubeSection;
